import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import CustomIcon from '@components/shared/CustomIcon';
import IconButton from '@components/shared/IconButton/IconButton';
import { Row, Col } from '@components/antd';
import { LandlordBuilding } from '@root/types';
import routes from '@root/routes';
import cn from 'classnames';
import BuildingLink from './BuildingLink';
import s from './BuildingCard.module.less';

type BuildingCardProps = {
  building: LandlordBuilding;
  onBuildingClick?: () => void;
  showSaveButton?: boolean;
  isSaved?: boolean;
  onSave?: (id: string, slug: string) => void;
  onUnsave?: (id: string, slug: string) => void;
  minimal?: boolean;
};

const BuildingCard = ({
  building,
  onBuildingClick = () => null,
  showSaveButton = false,
  isSaved = false,
  onSave = () => null,
  onUnsave = () => null,
  minimal = false,
}: BuildingCardProps) => {
  const { t } = useTranslation('landlord');

  const toggleSave = () => {
    if ('slug' in building) {
      if (isSaved) {
        onUnsave(building.id, building.slug);
      } else {
        onSave(building.id, building.slug);
      }
    }
  };

  const renderBuildingCardLocation = () => (
    <div className={s.location}>
      {building.location}
      <span className={s.locationDivider}>|</span>
      {t(`country:${building.countryCode}`)}
    </div>
  );
  return (
    <div className={s.buildingCard}>
      {showSaveButton && (
        <IconButton
          className={cn({ [s.heartIcon]: isSaved, [s.heartButton]: true })}
          icon={isSaved ? 'heart' : 'heart-outline'}
          onClick={toggleSave}
          title={t('saveToFavorites')}
        />
      )}
      <NavLink to={routes.building(building.slug)} className={s.link} onClick={onBuildingClick}>
        {!minimal ? (
          <CustomIcon type="vts-logo-indigo" className={s.logo} data-testid="vtsLogo" />
        ) : null}
        <Row className={s.row}>
          <Col span={12} className={s.imageCol}>
            <img className={s.image} src={building.photoUrl} alt={building.photoDescription} />
          </Col>
          <Col span={12} className={s.info}>
            <div className={!minimal ? s.name : s.nameLighter}>
              <div className={s.truncateName} title={building.primaryName}>
                {building.primaryName}
              </div>
              <div className={s.truncateName} title={building.secondaryName || undefined}>
                {building.secondaryName}
              </div>
            </div>
            <div className={s.location}>{renderBuildingCardLocation()}</div>
            {!minimal && <div className={s.area}>{t('units:area', { area: building.area })}</div>}
            {building.availableArea && !minimal && building.availableArea.magnitude > 100 ? (
              <div className={s.area}>
                {t('building.availableArea', {
                  ...building.availableArea,
                  count: building.availableArea?.magnitude || 0,
                })}
              </div>
            ) : null}
            <div className={s.spacer} />
            <BuildingLink numberOfAvailabilities={building.numberOfAvailabilities} />
          </Col>
        </Row>
      </NavLink>
    </div>
  );
};

export default BuildingCard;
